import React from 'react';
import Grid from '@mui/material/Grid';
import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import Typography from '@mui/material/Typography';
import Box from '@mui/material/Box';
import Alert from '@mui/material/Alert';
import Chip from '@mui/material/Chip';
import List from '@mui/material/List';
import ListItem from '@mui/material/ListItem';
import ListItemText from '@mui/material/ListItemText';
import { Doughnut } from 'react-chartjs-2';
import { Chart as ChartJS, ArcElement, Tooltip, Legend } from 'chart.js';
import { useRules } from '../context/RuleContext';

ChartJS.register(ArcElement, Tooltip, Legend);

function StatCard({ title, value, color, subtitle }) {
  return (
    <Card>
      <CardContent sx={{ textAlign: 'center', py: 3 }}>
        <Typography variant="h3" sx={{ fontWeight: 700, color }}>{value}</Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>{title}</Typography>
        {subtitle && <Typography variant="caption" color="text.disabled">{subtitle}</Typography>}
      </CardContent>
    </Card>
  );
}

export default function Overview() {
  const { feeRules, locationGroups, carrierGroups, testRuleSet, changeLog } = useRules();

  const suppressed = feeRules.filter(r => r.suppress === 'true' || r.suppress === true).length;
  const discounts = feeRules.filter(r => {
    if (r.feePercent && parseFloat(r.feePercent) < 0) return true;
    if (r.feeAmount && r.feeAmount.includes('-')) return true;
    return false;
  }).length;
  const fees = feeRules.length - suppressed - discounts;

  const now = new Date();
  const expiringCount = feeRules.filter(r => {
    if (!r.travelDateDiscontinue) return false;
    const d = new Date(r.travelDateDiscontinue);
    return d >= now && d <= new Date(now.getTime() + 30 * 24 * 60 * 60 * 1000);
  }).length;

  const ruleTypeData = {
    labels: ['Markups', 'Discounts', 'Suppression'],
    datasets: [{
      data: [fees, discounts, suppressed],
      backgroundColor: ['#4caf50', '#ff9800', '#f44336'],
    }],
  };

  return (
    <Box>
      <Typography variant="h5" sx={{ fontWeight: 600, mb: 3 }}>Dashboard</Typography>

      {feeRules.length === 0 && (
        <Alert severity="info" sx={{ mb: 3 }}>
          <strong>Welcome!</strong> Get started by uploading an XML fee rules file or fetching from Bitbucket.
          Navigate to "Upload XML" or "Bitbucket" in the sidebar.
        </Alert>
      )}

      {/* Stats */}
      <Grid container spacing={2} sx={{ mb: 4 }}>
        <Grid item xs={6} sm={4} md={2}>
          <StatCard title="Fee Rules" value={feeRules.length} color="#1976d2" />
        </Grid>
        <Grid item xs={6} sm={4} md={2}>
          <StatCard title="Location Groups" value={locationGroups.length} color="#4caf50" />
        </Grid>
        <Grid item xs={6} sm={4} md={2}>
          <StatCard title="Carrier Groups" value={carrierGroups.length} color="#ff9800" />
        </Grid>
        <Grid item xs={6} sm={4} md={2}>
          <StatCard title="Suppressed" value={suppressed} color="#f44336" />
        </Grid>
        <Grid item xs={6} sm={4} md={2}>
          <StatCard title="Discounts" value={discounts} color="#9c27b0" />
        </Grid>
        <Grid item xs={6} sm={4} md={2}>
          <StatCard title="Expiring (30d)" value={expiringCount} color="#e91e63" />
        </Grid>
      </Grid>

      <Grid container spacing={3}>
        {/* Chart */}
        <Grid item xs={12} md={4}>
          <Card sx={{ height: '100%' }}>
            <CardContent>
              <Typography variant="h6" sx={{ mb: 2 }}>Rule Types</Typography>
              {feeRules.length > 0 ? (
                <Doughnut data={ruleTypeData} options={{ responsive: true, plugins: { legend: { position: 'bottom' } } }} />
              ) : (
                <Box sx={{ textAlign: 'center', py: 4 }}>
                  <Typography color="text.secondary">No data yet</Typography>
                </Box>
              )}
            </CardContent>
          </Card>
        </Grid>

        {/* Quick Info */}
        <Grid item xs={12} md={4}>
          <Card sx={{ height: '100%' }}>
            <CardContent>
              <Typography variant="h6" sx={{ mb: 2 }}>Quick Info</Typography>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                  <Typography variant="body2">Unique Carriers</Typography>
                  <Chip label={new Set(feeRules.map(r => r.carrier).filter(c => c !== '**')).size} size="small" />
                </Box>
                <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                  <Typography variant="body2">Unique Origins</Typography>
                  <Chip label={new Set(feeRules.map(r => r.origin).filter(Boolean)).size} size="small" />
                </Box>
                <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                  <Typography variant="body2">Rules with Dates</Typography>
                  <Chip label={feeRules.filter(r => r.travelDateEffective || r.travelDateDiscontinue).length} size="small" />
                </Box>
                <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                  <Typography variant="body2">Test Rules</Typography>
                  <Chip label={testRuleSet?.feeRules?.length || 0} size="small" />
                </Box>
                <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                  <Typography variant="body2">GDS Rules</Typography>
                  <Chip label={feeRules.filter(r => r.priceSource === 'GDS').length} size="small" />
                </Box>
                <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                  <Typography variant="body2">Website Rules</Typography>
                  <Chip label={feeRules.filter(r => r.priceSource === 'WEBSITE').length} size="small" />
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>

        {/* Recent Changes */}
        <Grid item xs={12} md={4}>
          <Card sx={{ height: '100%' }}>
            <CardContent>
              <Typography variant="h6" sx={{ mb: 2 }}>Recent Changes</Typography>
              {changeLog.length === 0 ? (
                <Typography color="text.secondary" variant="body2">No changes yet</Typography>
              ) : (
                <List dense disablePadding>
                  {changeLog.slice(0, 5).map(entry => (
                    <ListItem key={entry.id} disablePadding sx={{ mb: 1 }}>
                      <ListItemText
                        primary={entry.details}
                        secondary={new Date(entry.timestamp).toLocaleString()}
                        primaryTypographyProps={{ variant: 'body2', noWrap: true }}
                        secondaryTypographyProps={{ variant: 'caption' }}
                      />
                    </ListItem>
                  ))}
                </List>
              )}
            </CardContent>
          </Card>
        </Grid>
      </Grid>
    </Box>
  );
}
